import { createAuthSession, LocalStorageTokenStorage, TokenManager } from "@ametie/vue-muza-use";
import type { RefreshResult } from "@ametie/vue-muza-use";
import { useToast } from "@app/core";
import { isAxiosError } from "axios";
import { readonly, ref } from "vue";

import { authSession } from "./authSession";
import { myAxios } from "./axios";

// The Tools token: a second token store and auth session next to the insights
// one. Insights mints the token (`POST /auth/so-tools-token`, sent with the
// insights token); Tools transports send it. Nothing else in the app reads it.
// A failure here clears only this store and never logs out of insights.
// Design: openspec/changes/archive/2026-10-15-add-tools-token-auth (D1-D3, D6).

export const TOOLS_TOKEN_URL = "/auth/so-tools-token";

// Renew a token that expires within this window, before a request is sent.
const RENEW_AHEAD_MS = 60_000;

export const TOOLS_FORBIDDEN_MESSAGE = "Your account does not have access to Tools";
export const TOOLS_UNAVAILABLE_MESSAGE = "Tools is temporarily unavailable";
export const TOOLS_FAILED_MESSAGE = "Could not get a Tools access token";

export type ToolsTokenFailure = "forbidden" | "unavailable" | "failed";
export type ToolsAuthState = "ok" | "forbidden" | "unavailable";

const FAILURE_MESSAGES: Record<ToolsTokenFailure, string> = {
  forbidden: TOOLS_FORBIDDEN_MESSAGE,
  unavailable: TOOLS_UNAVAILABLE_MESSAGE,
  failed: TOOLS_FAILED_MESSAGE,
};

export class ToolsTokenError extends Error {
  readonly reason: ToolsTokenFailure;

  constructor(reason: ToolsTokenFailure) {
    super(FAILURE_MESSAGES[reason]);
    this.name = "ToolsTokenError";
    this.reason = reason;
  }
}

interface ToolsTokenResponse {
  access_token: string
  expires_at: number
}

const isToolsTokenResponse = (body: unknown): body is ToolsTokenResponse =>
  typeof body === "object"
  && body !== null
  && "access_token" in body
  && typeof body.access_token === "string"
  && "expires_at" in body
  && typeof body.expires_at === "number";

const toFailure = (error: unknown): ToolsTokenError => {
  if (error instanceof ToolsTokenError) return error;
  if (isAxiosError(error)) {
    if (error.response?.status === 403) return new ToolsTokenError("forbidden");
    if (error.response?.status === 503) return new ToolsTokenError("unavailable");
  }
  return new ToolsTokenError("failed");
};

export const toolsTokens = new TokenManager(new LocalStorageTokenStorage({
  keys: {
    accessToken: "toolsAccessToken",
    refreshToken: "toolsRefreshToken",
    expiresAt: "toolsTokenExpiresAt",
  },
  storeRefreshToken: false,
}));

// Goes through `myAxios`, which is attached to the insights session: an expired
// insights token is refreshed and the call retried, and a failed insights
// refresh logs out exactly as for any other insights request.
const mintToolsToken = async (): Promise<RefreshResult> => {
  try {
    const { data } = await myAxios.post<unknown>(TOOLS_TOKEN_URL);
    if (!isToolsTokenResponse(data)) throw new ToolsTokenError("failed");
    return {
      accessToken: data.access_token,
      expiresIn: data.expires_at - Date.now() / 1000,
    };
  }
  catch (error) {
    throw toFailure(error);
  }
};

export const toolsSession = createAuthSession({
  refresh: mintToolsToken,
  tokens: toolsTokens,
});

// Single-flight through the session: concurrent callers share one mint. A token
// without a stored expiry counts as needing renewal.
export const ensureToolsToken = async (): Promise<void> => {
  const expiresAt = toolsTokens.getTokenExpiresAt();
  if (
    toolsTokens.getAccessToken()
    && expiresAt !== null
    && expiresAt - RENEW_AHEAD_MS > Date.now()
  ) return;
  await toolsSession.refresh();
};

const state = ref<ToolsAuthState>("ok");

export const toolsAuthState = readonly(state);

toolsSession.on("refreshed", () => {
  state.value = "ok";
});

toolsSession.on("refreshFailed", ({ error }) => {
  if (!(error instanceof ToolsTokenError)) return;

  if (error.reason === "forbidden") {
    state.value = "forbidden";
  }
  else if (error.reason === "unavailable") {
    state.value = "unavailable";
    // Resolved lazily: the toast host is not available while this module loads.
    useToast().error(TOOLS_UNAVAILABLE_MESSAGE);
  }
  // Without an insights token the insights refresh failed and the user is being logged out:
  // that is the message, not this one.
  else if (authSession.getAccessToken() !== null) {
    useToast().error(TOOLS_FAILED_MESSAGE);
  }
});

authSession.on("cleared", () => toolsSession.clear());
authSession.on("refreshFailed", () => toolsSession.clear());
